/**
 * GameDashboard Component
 * 
 * Main dashboard that orchestrates the game-like UI.
 * Combines header, date banner, view toggle, and content area.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { GameHeader } from './GameHeader';
import { GameDateBanner } from './GameDateBanner';
import { GameViewToggle, GameView } from './GameViewToggle';
import { GameListView } from './views/GameListView';
import { GameCalendarView } from './views/GameCalendarView';
import { GameDrawer } from './GameDrawer';
// Use original clock components for proper wedge rendering
import { ClockView } from '../Dashboard/ClockView';
import { SwipeableContent } from '../SwipeableContent/SwipeableContent';
import { CreateRoutineModal } from '../Routine/CreateRoutineModal';
import { TaskDetailModal } from '../TaskDetailModal/TaskDetailModal';
import { useTasks } from '../../hooks/queries/useTasks';
import { useRoutines } from '../../hooks/queries/useRoutines';
import { useProfiles } from '../../hooks/queries/useProfiles';
import { useSettings } from '../../hooks/queries/useSettings';
import { useTime } from '../../hooks/useTime';
import { createTimeSource } from '../../utils/timeSource';
import { shadeForIndex } from '../../utils/colorUtils';
import { Task } from '../../types';
import styles from './GameDashboard.module.scss';

export const GameDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [currentView, setCurrentView] = useState<GameView>('clock'); // Default to clock view
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null);
  const [showRoutineModal, setShowRoutineModal] = useState(false);
  const [showNeedChildPrompt, setShowNeedChildPrompt] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  // Get profiles and settings first
  const { profiles } = useProfiles();
  const { settings, updateSettings } = useSettings();

  // Time source for clock - use the Test Preview time when test mode is on
  const timeSource = useMemo(() => createTimeSource('live'), []);
  const liveTime = useTime(timeSource);

  // Use the Test Preview time (set in Settings) when test mode is active
  const currentTime = useMemo(() => {
    if (settings?.testMode) {
      const now = new Date();
      const previewMinutes = settings.testPreviewMinutes ?? (now.getHours() * 60 + now.getMinutes());
      const simDate = new Date();
      simDate.setHours(Math.floor(previewMinutes / 60), previewMinutes % 60, 0, 0);
      return simDate;
    }
    return liveTime;
  }, [settings?.testMode, settings?.testPreviewMinutes, liveTime]);
  
  // Derive active profile
  useEffect(() => {
    if (!activeProfileId && profiles.length > 0) {
      setActiveProfileId(profiles[0].id);
    }
  }, [profiles, activeProfileId]);

  const activeProfile = useMemo(() => 
    profiles.find(p => p.id === activeProfileId) || null,
    [profiles, activeProfileId]
  );

  // Data hooks - these require profileId
  const { tasks, loading: tasksLoading, createTask, updateTask, deleteTask } = useTasks(activeProfileId);
  const { profileRoutines, routinesLoading } = useRoutines(activeProfileId || undefined);

  // Transform tasks to the format expected by ClockView
  const clockTasks: Task[] = useMemo(() => {
    return tasks?.map(task => ({
      id: task.id,
      profileId: task.profileId,
      routineId: task.routineId,
      label: task.label,
      icon: task.icon || '📋',
      color: task.color || '#5C6DB0',
      start: task.start,
      end: task.end,
      order_index: task.order_index,
    })) || [];
  }, [tasks]);

  // Group tasks by routine for list view
  const formattedRoutines = useMemo(() => (
    profileRoutines?.map((routine) => ({
      id: routine.id,
      name: routine.name,
      color: routine.color || '#5C6DB0',
      tasks: routine.tasks || [],
    })) || []
  ), [profileRoutines]);

  const hasAnyTasks = clockTasks.length > 0 || formattedRoutines.some(r => r.tasks.length > 0);
  const isLoading = tasksLoading || routinesLoading;

  const handleMenuClick = useCallback(() => {
    setIsDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const handleProfileClick = useCallback(() => {
    setIsDrawerOpen(true);
  }, []);

  // View order for swipe navigation
  const viewOrder: GameView[] = useMemo(() => ['clock', 'list', 'calendar'], []);
  const currentViewIndex = viewOrder.indexOf(currentView);

  // Swipe navigation between views (not profiles)
  const handleSwipeLeft = useCallback(() => {
    // Go to next view
    if (currentViewIndex < viewOrder.length - 1) {
      setCurrentView(viewOrder[currentViewIndex + 1]);
    }
  }, [currentViewIndex, viewOrder]);

  const handleSwipeRight = useCallback(() => {
    // Go to previous view
    if (currentViewIndex > 0) {
      setCurrentView(viewOrder[currentViewIndex - 1]);
    }
  }, [currentViewIndex, viewOrder]);

  const handleViewChange = useCallback((view: GameView) => {
    setCurrentView(view);
  }, []);

  const handleDateChange = useCallback((date: Date) => {
    setSelectedDate(date);
  }, []);

  const handleCreateRoutine = useCallback(() => {
    // A routine must belong to a child. If none exist yet, send the parent to
    // create one first instead of opening a wizard that can't be saved.
    if (profiles.length === 0) {
      setShowNeedChildPrompt(true);
      return;
    }
    setShowRoutineModal(true);
  }, [profiles.length]);

  const handleRoutineModalClose = useCallback(() => {
    setShowRoutineModal(false);
  }, []);

  // For list/calendar views - receives taskId
  const handleTaskClick = useCallback((taskId: string) => {
    const task = clockTasks.find(t => t.id === taskId);
    if (task) {
      setEditingTask(task);
    }
  }, [clockTasks]);

  // For clock view - receives task object
  const handleClockTaskClick = useCallback((task: Task) => {
    setEditingTask(task);
  }, []);

  const handleTaskSave = useCallback(async (updatedTask: Task) => {
    try {
      const isNew = updatedTask.id.startsWith('task-');
      if (isNew) {
        await createTask(updatedTask);
      } else {
        await updateTask({ id: updatedTask.id, updates: updatedTask });
      }
      setEditingTask(null);
    } catch (error) {
      console.error("Failed to save task:", error);
    }
  }, [createTask, updateTask]);

  const handleTaskDelete = useCallback(async (taskId: string) => {
    try {
      await deleteTask(taskId);
      setEditingTask(null);
    } catch (error) {
      console.error("Failed to delete task:", error);
    }
  }, [deleteTask]);

  const handleAddTask = useCallback((routineId: string) => {
    if (!activeProfileId) return;

    const routine = formattedRoutines.find(r => r.id === routineId);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const routineTasks: any[] = routine?.tasks || [];
    const routineColor = routine?.color || '#5C6DB0';

    // A new task chains onto the end of the routine: it starts where the latest
    // existing task ends (HH:MM strings sort lexically when zero-padded).
    const lastEnd = routineTasks.reduce<string>((latest, t) => {
      const end: string = t.end || t.end_time || '';
      return end > latest ? end : latest;
    }, '');
    const start = lastEnd || '09:00';
    const [sh, sm] = start.split(':').map(Number);
    const endTotal = sh * 60 + sm + 30;
    const end = `${String(Math.floor(endTotal / 60) % 24).padStart(2, '0')}:${String(endTotal % 60).padStart(2, '0')}`;

    const newTask: Task = {
      id: `task-${Date.now()}`,
      profileId: activeProfileId,
      routineId,
      label: '',
      icon: '📋',
      // Task color is auto-derived as a shade of the routine color.
      color: shadeForIndex(routineColor, routineTasks.length),
      start,
      end,
      order_index: routineTasks.length,
    };
    setEditingTask(newTask);
  }, [activeProfileId, formattedRoutines]);

  const renderCurrentView = () => {
    switch (currentView) {
      case 'list':
        return (
          <GameListView 
            routines={formattedRoutines}
            tasks={clockTasks}
            selectedDate={selectedDate}
            hasAnyTasks={hasAnyTasks}
            isLoading={isLoading}
            onAddRoutine={handleCreateRoutine}
            onTaskEdit={handleTaskClick}
            onAddTask={handleAddTask}
          />
        );
      case 'clock':
        // Use original ClockView for proper wedge rendering
        return (
          <div className={styles.clockViewWrapper}>
            <ClockView 
              time={currentTime}
              tasks={clockTasks}
              onTaskClick={handleClockTaskClick}
              themeId={settings?.clockThemeId || 'playful_focus'}
              showPastTasks={settings?.showPastTasks ?? true}
              showMinuteTicks={settings?.showMinuteTicks ?? false}
              taskSortingMode={settings?.taskSortingMode || 'default'}
            />
          </div>
        );
      case 'calendar':
        return (
          <GameCalendarView 
            routines={formattedRoutines}
            tasks={clockTasks}
            selectedDate={selectedDate}
            hasAnyTasks={hasAnyTasks}
            isLoading={isLoading}
            onTaskClick={handleTaskClick}
          />
        );
      default:
        return (
          <GameListView 
            routines={formattedRoutines}
            tasks={clockTasks}
            selectedDate={selectedDate}
            hasAnyTasks={hasAnyTasks}
            isLoading={isLoading}
          />
        );
    }
  };

  return (
    <div className={styles.gameDashboard}>
      <GameHeader 
        onMenuClick={handleMenuClick}
        onProfileClick={handleProfileClick}
        activeProfile={activeProfile}
      />
      
      {/* Date banner + view toggle + add-routine stay fixed; only the view
          content below swipes/scrolls. This keeps the cream header from
          sliding away (and exposing the background) mid-swipe. */}
      <GameDateBanner
        selectedDate={selectedDate}
        onDateChange={handleDateChange}
      />
      <div className={styles.headerArea}>
        <GameViewToggle
          currentView={currentView}
          onViewChange={handleViewChange}
        />
      </div>

      <main className={styles.mainContent}>
        {/* Add routine button - consistent across all views */}
        <div className={styles.addRoutineWrapper}>
          <button
            className={styles.addRoutineButton}
            onClick={handleCreateRoutine}
            type="button"
          >
            + Add routine
          </button>
        </div>

        {/* Test Preview — enabled in Settings, but the time is dragged here, on the
            clock screen, so you can watch the clock change as you scrub. */}
        {settings?.testMode && currentView === 'clock' && (
          <div className={styles.testModeBanner}>
            <div className={styles.testModeTopRow}>
              <span className={styles.testModeLabel}>
                🧪 Test Preview —{' '}
                {String(Math.floor((settings.testPreviewMinutes ?? 0) / 60)).padStart(2, '0')}:
                {String((settings.testPreviewMinutes ?? 0) % 60).padStart(2, '0')}
              </span>
              <button
                type="button"
                className={styles.testModeExit}
                onClick={() => updateSettings({ testMode: false })}
              >
                Exit
              </button>
            </div>
            <input
              type="range"
              min={0}
              max={1439}
              value={settings.testPreviewMinutes ?? 0}
              onChange={(e) => updateSettings({ testPreviewMinutes: parseInt(e.target.value, 10) })}
              className={styles.testModeSlider}
              aria-label="Preview time"
            />
          </div>
        )}

        <SwipeableContent
          onSwipeLeft={handleSwipeLeft}
          onSwipeRight={handleSwipeRight}
          currentIndex={currentViewIndex}
          totalItems={viewOrder.length}
          disabled={false}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentView}
              className={styles.viewContainer}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {renderCurrentView()}
            </motion.div>
          </AnimatePresence>
        </SwipeableContent>
      </main>

      {/* Dot Indicators - rendered outside SwipeableContent to avoid overflow:hidden clipping */}
      {viewOrder.length > 1 && (
        <div className={styles.dotIndicatorsOverlay}>
          {viewOrder.map((_, i) => (
            <div 
              key={i}
              className={`${styles.dotIndicator} ${i === currentViewIndex ? styles.dotActive : ''}`}
            />
          ))}
        </div>
      )}

      <GameDrawer
        isOpen={isDrawerOpen} 
        onClose={handleDrawerClose}
        profiles={profiles}
        activeProfile={activeProfile}
        onProfileSelect={setActiveProfileId}
      />

      {/* Prompt to add a child before any routine can be created */}
      {showNeedChildPrompt && (
        <div className={styles.promptOverlay} onClick={() => setShowNeedChildPrompt(false)}>
          <div className={styles.promptCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.promptEmoji}>👶</div>
            <h3 className={styles.promptTitle}>Add a child first</h3>
            <p className={styles.promptText}>
              Routines belong to a child profile. Create one and you'll be ready to plan their day.
            </p>
            <div className={styles.promptActions}>
              <button
                className={styles.promptSecondary}
                onClick={() => setShowNeedChildPrompt(false)}
                type="button"
              >
                Not now
              </button>
              <button
                className={styles.promptPrimary}
                onClick={() => {
                  setShowNeedChildPrompt(false);
                  navigate('/app/profile');
                }}
                type="button"
              >
                + Add Child
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Routine Modal */}
      {showRoutineModal && activeProfileId && (
        <CreateRoutineModal
          profileId={activeProfileId}
          onClose={handleRoutineModalClose}
        />
      )}

      {/* Task Detail Modal */}
      <AnimatePresence>
        {editingTask && (
          <TaskDetailModal
            task={editingTask}
            allTasks={clockTasks}
            onSave={handleTaskSave}
            onClose={() => setEditingTask(null)}
            onDelete={handleTaskDelete}
            isRecurringSeries={false}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default GameDashboard;
